// CellRenderer/index.tsx
// AG Grid custom cell renderer with Notion/OpenAI-inspired pending states.

import { useCallback, useEffect, useRef } from "react";

import CellPreview from "./CellPreview";
import { useCellRendererModel } from "./hooks";
import type { CellRendererParams } from "./types";

import { traceSmartTable } from "@/routes/smart-table/utils/smartTableTrace";

const CellRendererBase = (params: CellRendererParams) => {
  const { api, column, data, node, onSmartColumnDoubleClick } = params;
  const model = useCellRendererModel(params);

  // Ground truth for "did AG Grid remount this cell, or just patch it in
  // place": a row/column-scoped mount/unmount is only ever produced by row
  // node re-creation (block reload via `refreshServerSide`/`redrawRows`),
  // never by `applyServerSideTransaction` or `refreshCells`. Cross-reference
  // with `window.__dumpSmartTableTrace__({ category: "grid-op" })` to see
  // which grid-op immediately preceded a remount.
  const rowIndex =
    typeof data?._rowIndex === "number" ? data._rowIndex : (node.rowIndex ?? undefined);
  const columnId = column?.getColId();
  const initialStatusRef = useRef(model.previewProps.status);

  useEffect(() => {
    traceSmartTable({
      category: "renderer",
      op: "mount",
      source: "CellRenderer",
      rowIndex,
      columnId,
      detail: { status: initialStatusRef.current },
    });

    return () => {
      traceSmartTable({
        category: "renderer",
        op: "unmount",
        source: "CellRenderer",
        rowIndex,
        columnId,
      });
    };
    // Mount/unmount tracking only. rowIndex/columnId are fixed for the
    // lifetime of a given renderer instance (AG Grid creates a new instance
    // per row/column) — this must not re-fire on model or param updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNativeDoubleClick = useCallback(
    (event: MouseEvent) => {
      if (model.isHumanColumn) {
        event.stopPropagation();
        return;
      }

      onSmartColumnDoubleClick?.({
        api,
        column,
        data,
        event,
        rowIndex: node.rowIndex,
      });
    },
    [api, column, data, model.isHumanColumn, node, onSmartColumnDoubleClick],
  );

  const handleNativeDoubleClickRef = useRef(handleNativeDoubleClick);
  handleNativeDoubleClickRef.current = handleNativeDoubleClick;

  const handleDoubleClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    handleNativeDoubleClickRef.current(event.nativeEvent);
  }, []);

  useEffect(() => {
    if (!model.eGridCell) {
      return;
    }

    const gridCell = model.eGridCell;
    const handleGridCellDoubleClick = (event: MouseEvent) => {
      handleNativeDoubleClickRef.current(event);
    };

    gridCell.addEventListener("dblclick", handleGridCellDoubleClick, true);

    return () => {
      gridCell.removeEventListener("dblclick", handleGridCellDoubleClick, true);
    };
  }, [model.eGridCell]);

  useEffect(() => {
    if (!model.eGridCell) {
      return;
    }

    const gridCell = model.eGridCell;
    gridCell.classList.toggle("smart-table-cell-stale", model.isVisuallyStale);

    gridCell.classList.toggle("smart-table-cell-compare", model.isCompare);
    gridCell.classList.toggle("smart-table-cell-assertion", model.isLLMAssertion);
    gridCell.classList.toggle("smart-table-cell-error", model.previewProps.isError);
    gridCell.classList.toggle("smart-table-cell-truthy", model.computedBooleanValue === true);
    gridCell.classList.toggle("smart-table-cell-falsy", model.computedBooleanValue === false);
    gridCell.classList.toggle(
      "smart-table-cell-status-filter-active",
      model.statusFilterHighlightTone === "active",
    );
    gridCell.classList.toggle(
      "smart-table-cell-status-filter-failed",
      model.statusFilterHighlightTone === "failed",
    );
    gridCell.classList.toggle(
      "smart-table-cell-status-filter-completed",
      model.statusFilterHighlightTone === "completed",
    );
    gridCell.classList.remove("smart-table-cell-idle-empty");
  }, [
    model.eGridCell,
    model.isVisuallyStale,
    model.isCompare,
    model.isLLMAssertion,
    model.previewProps.isError,
    model.computedBooleanValue,
    model.statusFilterHighlightTone,
  ]);

  return (
    <div className="h-full w-full" onDoubleClick={handleDoubleClick}>
      <CellPreview {...model.previewProps} />
    </div>
  );
};

CellRendererBase.displayName = "CellRenderer";

export default CellRendererBase;
